import { PreformatifDiagnosticAnswers } from "@/interfaces/PreformatifDiagnostic";

/**
 * Questionnaire « Diagnostic préformatif entreprise — Questionnaire pour managers ».
 * Copie de `API/app/Services/PreformatifDiagnosticQuestionnaire.ts`, qui fait foi
 * (finalisation et export PDF) : modifier l'une sans l'autre les désaligne.
 */
export interface Choice {
  key: string;
  label: string;
}

export const CONTEXT_OPTIONS: Choice[] = [
  { key: "immediate_need", label: "Besoin opérationnel immédiat" },
  { key: "job_evolution", label: "Évolution du poste" },
  { key: "upskilling", label: "Montée en compétences" },
  { key: "tool_change", label: "Changement d’outil / logiciel" },
  { key: "regulatory", label: "Obligation réglementaire" },
  { key: "difficulties", label: "Difficultés identifiées" },
  { key: "other", label: 'Autre (préciser dans le champ "observations")' },
];

export const MODULE_ROWS: Choice[] = Array.from({ length: 12 }, (_, index) => ({
  key: String(index + 1),
  label: `Module ${index + 1}`,
}));

export const MODULE_CHOICES: Choice[] = [
  { key: "yes", label: "Oui" },
  { key: "optional", label: "Optionnel, si possible" },
];

export const LEVEL_OPTIONS: Choice[] = [
  { key: "beginner", label: "Débutant" },
  { key: "intermediate", label: "Intermédiaire" },
  { key: "advanced", label: "Avancé" },
  { key: "homogeneous", label: "Niveau du groupe : Homogène" },
  { key: "heterogeneous", label: "Niveau du groupe : Hétérogène" },
];

export const PREREQUISITES_OPTIONS: Choice[] = [
  { key: "yes", label: "Oui" },
  { key: "no", label: "Non" },
  { key: "depends", label: "Ça dépend" },
];

export const PSH_OPTIONS: Choice[] = [
  { key: "yes", label: "Oui" },
  { key: "no", label: "Non" },
  { key: "nsp", label: "NSP" },
];

export const MODALITY_OPTIONS: Choice[] = [
  { key: "onsite", label: "Présentiel" },
  { key: "remote", label: "Distanciel" },
  { key: "company", label: "Dans l'entreprise" },
  { key: "rented_place", label: "Dans un lieu loué pour l'occasion" },
  { key: "training_organisation", label: "Dans l'organisme de formation" },
];

export const MEANS_ROWS: Choice[] = [
  { key: "room", label: "Salle dédiée" },
  { key: "internet", label: "Connexion internet" },
  { key: "visio", label: "Maîtrise des outils de visio" },
  { key: "projector", label: "Vidéoprojecteur / écran" },
  { key: "computers", label: "Postes informatiques" },
  { key: "software", label: "Logiciels adéquats" },
  {
    key: "equipment_list",
    label: "Liste du matériel nécessaire fournie (si applicable)",
  },
];

export const MEANS_CHOICES: Choice[] = [
  { key: "yes", label: "Oui" },
  { key: "no", label: "Non" },
];

export const HINTS = {
  multiple: "Plusieurs réponses possibles",
  single: "Une seule réponse possible",
  grid: "Cochez une case par ligne",
};

/** Intitulés des questions, dans l'ordre du modèle. */
export const QUESTIONS: Record<keyof PreformatifDiagnosticAnswers, string> = {
  respondentName: "Nom :",
  context: "Quel est le contexte de cette demande de formation",
  observations: "Observations",
  objectives: "Quels sont les objectifs/résultats attendus :",
  modules:
    "Pour nous permettre de répondre au mieux à vos besoins et de personnaliser votre programme de formation, veuillez nous préciser quels modules vous intéressent ? (cf programme de formation général)",
  headcount: "Quel est l'effectif à former :",
  levels: "Quel est le niveau du/des salarié(s) à former :",
  levelObservations: "Avez-vous des observations à nous partager ?",
  prerequisites:
    "Le(s) salarié(s) dispose(nt)-il(s) des prérequis nécessaires ?",
  prerequisitesPrecision:
    'Veuillez nous donner plus de précisions en cas de réponse "non" ou "ça dépend" :',
  psh: "Un ou plusieurs salariés sont-ils en situation de handicap (PSH) ?",
  pshPrecision:
    "Veuillez nous apporter des précisions (nom, handicap, aménagement nécessaire) :",
  modalities: "Quelles modalités souhaitez-vous :",
  means:
    "De quels moyens disposez-vous pour accueillir cette formation (à compléter selon applicabilité) :",
  materialComments: "Avez-vous des commentaires / contraintes matérielles :",
  planningConstraints: "Avez-vous des contraintes de planning :",
  finalObservations: "Avez-vous des observations :",
};

/** Libellés courts, pour nommer les réponses manquantes à la finalisation. */
export const FIELD_LABELS: Record<keyof PreformatifDiagnosticAnswers, string> =
  {
    respondentName: "Nom",
    context: "Contexte de la demande",
    observations: "Observations",
    objectives: "Objectifs / résultats attendus",
    modules: "Modules",
    headcount: "Effectif à former",
    levels: "Niveau des salariés",
    levelObservations: "Observations sur le niveau",
    prerequisites: "Prérequis",
    prerequisitesPrecision: "Précisions sur les prérequis",
    psh: "Situation de handicap",
    pshPrecision: "Précisions sur le handicap",
    modalities: "Modalités",
    means: "Moyens disponibles",
    materialComments: "Contraintes matérielles",
    planningConstraints: "Contraintes de planning",
    finalObservations: "Observations",
  };

export const emptyAnswers = (): PreformatifDiagnosticAnswers => ({
  respondentName: "",
  context: [],
  observations: "",
  objectives: "",
  modules: {},
  headcount: "",
  levels: [],
  levelObservations: "",
  prerequisites: null,
  prerequisitesPrecision: "",
  psh: null,
  pshPrecision: "",
  modalities: [],
  means: {},
  materialComments: "",
  planningConstraints: "",
  finalObservations: "",
});

/** Réponses exigées pour finaliser — mêmes règles que l'API. */
export const isRequired = (
  field: keyof PreformatifDiagnosticAnswers,
  answers: PreformatifDiagnosticAnswers,
) => {
  switch (field) {
    case "respondentName":
    case "context":
    case "objectives":
    case "modules":
    case "headcount":
    case "levels":
    case "prerequisites":
    case "psh":
    case "modalities":
      return true;
    case "observations":
      return answers.context.includes("other");
    case "prerequisitesPrecision":
      return (
        answers.prerequisites === "no" || answers.prerequisites === "depends"
      );
    case "pshPrecision":
      return answers.psh === "yes";
    default:
      return false;
  }
};
